import React, { useRef, useState, useEffect, useMemo } from 'react';
import { RefreshCw, ExternalLink, Maximize2, Minimize2, LayoutDashboard, FolderOpen, Users, Building2, AlertTriangle, Camera, X, Download, Save, Loader2 } from 'lucide-react';
import { useToast } from '../components/ToastContext';
import { FilesService, PraticaService } from '../services/apiService';
import { useAuth } from '../contexts/AuthContext';

const BASE = 'https://app.otpservice.io';

const NAV_LINKS = [
    { label: 'Dashboard',     path: '/' },
    { label: 'Pratiche',      path: '/dossiers' },
    { label: 'Firmatari',     path: '/signers' },
];

const FirmaDigitaleView: React.FC = () => {
    const frameRef = useRef<HTMLIFrameElement>(null);
    const [src, setSrc] = useState(BASE);
    const [blocked, setBlocked] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [isCapturing, setIsCapturing] = useState(false);
    const { addToast } = useToast();
    const { currentSedeId } = useAuth();

    // Dopo la cattura mostriamo l'anteprima e l'utente sceglie se scaricarla o salvarla in pratica.
    const [screenshotBlob, setScreenshotBlob] = useState<Blob | null>(null);
    const [screenshotUrl, setScreenshotUrl] = useState<string | null>(null);
    const [pratiche, setPratiche] = useState<any[]>([]);
    const [selectedPraticaId, setSelectedPraticaId] = useState<string>('');
    const [praticaQuery, setPraticaQuery] = useState('');
    const [isSavingToPratica, setIsSavingToPratica] = useState(false);

    const closeScreenshotModal = () => {
        if (screenshotUrl) URL.revokeObjectURL(screenshotUrl);
        setScreenshotUrl(null);
        setScreenshotBlob(null);
        setSelectedPraticaId('');
        setPraticaQuery('');
    };

    // Al cambio sede invalida la cache pratiche, così il prossimo screenshot rifà la GET filtrata.
    useEffect(() => {
        setPratiche([]);
        setSelectedPraticaId('');
        setPraticaQuery('');
    }, [currentSedeId]);

    const filteredPratiche = useMemo(() => {
        const q = praticaQuery.trim().toLowerCase();
        if (!q) return pratiche.slice(0, 50);
        return pratiche.filter((p: any) => {
            const name = (p.name || p.nome || '').toLowerCase();
            const cli = (p.clientName || p.cliente || '').toLowerCase();
            return name.includes(q) || cli.includes(q) || String(p.id).includes(q);
        }).slice(0, 50);
    }, [praticaQuery, pratiche]);

    // Con <select size={N}> e una sola option non selezionata, il click non genera
    // onChange e "Salva" resta disabilitato. Selezioniamo quindi la prima opzione,
    // anche quando la selezione corrente esce dalla lista dopo una nuova ricerca.
    useEffect(() => {
        if (filteredPratiche.length === 0) return;
        const stillVisible = filteredPratiche.some((p: any) => String(p.id) === String(selectedPraticaId));
        if (!selectedPraticaId || !stillVisible) {
            setSelectedPraticaId(String(filteredPratiche[0].id));
        }
    }, [filteredPratiche, selectedPraticaId]);

    const navigate = (path: string) => {
        const url = BASE + path;
        setSrc(url);
        if (frameRef.current) frameRef.current.src = url;
    };

    const reload = () => {
        if (frameRef.current) frameRef.current.src = frameRef.current.src;
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            try {
                const doc = frameRef.current?.contentDocument ?? frameRef.current?.contentWindow?.document;
                if (!doc || doc.body === null || doc.body.innerHTML.trim() === '') {
                    setBlocked(true);
                }
            } catch {
                // Cross-origin: si carica normalmente, non è un blocco
            }
        }, 5000);
        return () => clearTimeout(timer);
    }, [src]);

    // L'iframe punta a app.otpservice.io (cross-origin), quindi html2canvas non può
    // leggerne il DOM. Usiamo getDisplayMedia e catturiamo un singolo frame.
    const takeScreenshot = async () => {
        if (isCapturing) return;
        if (!navigator.mediaDevices?.getDisplayMedia) {
            addToast('Il browser non supporta la cattura dello schermo.', 'error');
            return;
        }
        setIsCapturing(true);
        let stream: MediaStream | null = null;
        try {
            // preferCurrentTab (Chrome/Edge) preseleziona la tab corrente nel dialogo.
            stream = await navigator.mediaDevices.getDisplayMedia({
                video: { displaySurface: 'browser' } as any,
                audio: false,
                preferCurrentTab: true,
            } as any);
            const video = document.createElement('video');
            video.srcObject = stream;
            video.muted = true;
            await video.play();
            // Aspetta un frame perché videoWidth/Height siano valorizzati
            await new Promise(resolve => requestAnimationFrame(resolve));
            const canvas = document.createElement('canvas');
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            canvas.getContext('2d')!.drawImage(video, 0, 0);
            const blob: Blob | null = await new Promise(res => canvas.toBlob(res, 'image/png', 0.95));
            if (!blob) throw new Error('blob nullo');
            // Apriamo l'anteprima invece di scaricare subito: l'utente sceglie poi
            // tra "Scarica" e "Salva in Documentazione Ufficio".
            const url = URL.createObjectURL(blob);
            setScreenshotBlob(blob);
            setScreenshotUrl(url);
            // Pratiche caricate al momento, filtrate per sede solo se ne è selezionata
            // una specifica (ADMIN con 'ALL' o null: nessun filtro).
            if (pratiche.length === 0) {
                try {
                    const params: any = { size: 500 };
                    if (typeof currentSedeId === 'number') params.sedeId = currentSedeId;
                    const res: any = await PraticaService.getAll(params);
                    const list = res?.data?.content || res?.data || (Array.isArray(res) ? res : []);
                    setPratiche(Array.isArray(list) ? list : []);
                } catch {
                    // L'anteprima resta usabile per "Scarica"; segnaliamo solo l'errore sulle pratiche.
                    addToast('Impossibile caricare la lista pratiche', 'error');
                }
            }
        } catch (e: any) {
            // Annullato dall'utente (NotAllowedError): nessun messaggio.
            if (e?.name !== 'NotAllowedError' && e?.name !== 'AbortError') {
                addToast('Errore durante lo screenshot: ' + (e?.message || ''), 'error');
            }
        } finally {
            stream?.getTracks().forEach(t => t.stop());
            setIsCapturing(false);
        }
    };

    const downloadScreenshot = () => {
        if (!screenshotBlob || !screenshotUrl) return;
        const a = document.createElement('a');
        const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
        a.href = screenshotUrl;
        a.download = `firma-screenshot-${ts}.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        addToast('Screenshot scaricato', 'success');
    };

    const saveScreenshotToPratica = async () => {
        if (!screenshotBlob || !selectedPraticaId || isSavingToPratica) return;
        setIsSavingToPratica(true);
        try {
            const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
            const file = new File([screenshotBlob], `firma-screenshot-${ts}.png`, { type: 'image/png' });
            // Stessa categoria usata da TabMedia per la documentazione ufficio (senza watermark).
            await FilesService.uploadToPratica(Number(selectedPraticaId), file, false, 'DOC. UFFICIO');
            addToast('Screenshot salvato nella Documentazione Ufficio della pratica', 'success');
            closeScreenshotModal();
        } catch (e: any) {
            addToast(e?.response?.data?.message || 'Errore durante il salvataggio', 'error');
        } finally {
            setIsSavingToPratica(false);
        }
    };

    const toggleFullscreen = () => {
        if (!document.fullscreenElement) {
            document.documentElement.requestFullscreen?.();
            setIsFullscreen(true);
        } else {
            document.exitFullscreen?.();
            setIsFullscreen(false);
        }
    };

    return (
        <div className="flex flex-col h-full bg-slate-900 overflow-hidden">

            {/* Barra superiore */}
            <div className="h-12 flex-shrink-0 flex items-center gap-2 px-4 bg-slate-800 border-b border-slate-700">
               
                <div className="flex-1" />

                <button
                    onClick={reload}
                    title="Ricarica"
                    className="w-8 h-8 flex items-center justify-center rounded-md border border-slate-600 text-slate-500 hover:text-slate-200 hover:border-slate-400 transition-all"
                >
                    <RefreshCw size={14} />
                </button>
                <button
                    onClick={takeScreenshot}
                    disabled={isCapturing}
                    title="Screenshot (cattura la scheda corrente)"
                    className="w-8 h-8 flex items-center justify-center rounded-md border border-slate-600 text-slate-500 hover:text-slate-200 hover:border-slate-400 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    <Camera size={14} />
                </button>
                <button
                    onClick={toggleFullscreen}
                    title="Schermo intero"
                    className="w-8 h-8 flex items-center justify-center rounded-md border border-slate-600 text-slate-500 hover:text-slate-200 hover:border-slate-400 transition-all"
                >
                    {isFullscreen ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
                </button>
                <a
                    href={BASE}
                    target="_blank"
                    rel="noreferrer"
                    className="px-3 py-1.5 rounded-md text-xs font-medium text-blue-300 bg-blue-500/10 border border-blue-400/30 hover:bg-blue-500/20 transition-all whitespace-nowrap flex items-center gap-1.5"
                >
                    <ExternalLink size={12} />
                    Apri in nuova tab
                </a>
            </div>

            {/* Iframe */}
            {!blocked ? (
                <iframe
                    ref={frameRef}
                    src={src}
                    allow="clipboard-read; clipboard-write"
                    sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox allow-downloads allow-modals"
                    onError={() => setBlocked(true)}
                    className="flex-1 w-full border-0 bg-white"
                    title="OTP Service"
                />
            ) : (
                <div className="flex-1 flex flex-col items-center justify-center gap-4 text-center px-8 bg-slate-900">
                    <AlertTriangle size={48} className="text-red-400" />
                    <div className="text-lg font-semibold text-red-400">OTP Service non può essere incorporato</div>
                    <div className="text-sm text-slate-400 max-w-md leading-relaxed">
                        Il sito <strong className="text-slate-300">app.otpservice.io</strong> blocca l'embedding tramite iframe
                        per motivi di sicurezza (<code className="text-xs bg-slate-800 px-1 py-0.5 rounded">X-Frame-Options</code>).
                    </div>
                    <a
                        href={BASE}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-2 px-6 py-2.5 rounded-lg bg-gradient-to-r from-blue-500 to-indigo-500 text-white text-sm font-semibold hover:opacity-90 transition-opacity"
                    >
                        Apri OTP Service ↗
                    </a>
                </div>
            )}

            {/* Modale anteprima screenshot */}
            {screenshotUrl && (
                <div
                    className="fixed inset-0 z-[10001] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
                    onClick={closeScreenshotModal}
                >
                    <div
                        className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[92vh] flex flex-col overflow-hidden"
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 shrink-0">
                            <div className="flex items-center gap-2">
                                <Camera size={18} className="text-blue-600"/>
                                <h3 className="font-bold text-slate-900 text-sm">Screenshot</h3>
                                <span className="text-[11px] text-slate-400">Anteprima — scegli cosa farne</span>
                            </div>
                            <button
                                onClick={closeScreenshotModal}
                                className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
                                title="Chiudi"
                            >
                                <X size={18}/>
                            </button>
                        </div>

                        <div className="flex-1 overflow-auto bg-slate-100 p-4">
                            <img
                                src={screenshotUrl}
                                alt="Screenshot"
                                className="max-w-full max-h-full mx-auto object-contain rounded-lg shadow-lg border border-slate-200"
                            />
                        </div>

                        <div className="border-t border-slate-200 p-4 bg-white shrink-0 space-y-3">
                            {/* Salva in pratica: ricerca + select */}
                            <div className="flex flex-col gap-1">
                                <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wide">
                                    Salva nella Documentazione Ufficio di:
                                </label>
                                <input
                                    type="text"
                                    placeholder="Cerca pratica per nome, cliente o ID…"
                                    value={praticaQuery}
                                    onChange={e => setPraticaQuery(e.target.value)}
                                    className="border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                                />
                                <select
                                    value={selectedPraticaId}
                                    onChange={e => setSelectedPraticaId(e.target.value)}
                                    size={5}
                                    className="border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 bg-white"
                                >
                                    {filteredPratiche.length === 0 && (
                                        <option value="" disabled>Nessuna pratica trovata</option>
                                    )}
                                    {filteredPratiche.map((p: any) => {
                                        const name = p.name || p.nome || `Mandato #${p.id}`;
                                        const cli = p.clientName || p.cliente || '';
                                        return (
                                            <option key={p.id} value={p.id}>
                                                #{p.id} · {name}{cli ? ` — ${cli}` : ''}
                                            </option>
                                        );
                                    })}
                                </select>
                            </div>

                            <div className="flex items-center gap-2 justify-end">
                                <button
                                    onClick={downloadScreenshot}
                                    className="px-4 py-2 text-sm font-bold text-slate-700 hover:bg-slate-100 rounded-lg border border-slate-300 flex items-center gap-1.5 transition-colors"
                                >
                                    <Download size={14}/> Scarica
                                </button>
                                <button
                                    onClick={saveScreenshotToPratica}
                                    disabled={!selectedPraticaId || isSavingToPratica}
                                    className="px-4 py-2 text-sm font-bold bg-blue-600 text-white hover:bg-blue-700 rounded-lg flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    {isSavingToPratica
                                        ? <Loader2 size={14} className="animate-spin"/>
                                        : <Save size={14}/>}
                                    Salva in Documentazione Ufficio
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FirmaDigitaleView;
